/**
 * Playwright Test Generator
 *
 * Core module that transforms a machine trace JSON into a Playwright test file.
 * Uses the DOM events captured by the spy for accurate test generation.
 *
 * Design principles:
 * 1. DOM events are the source of truth - use them directly, no shortcuts
 * 2. Include metadata for traceability (trace ID, timestamps, original state)
 * 3. Assert on final context snapshot at the end
 * 4. Encrypt sensitive input data using env key to prevent PII in logs
 */

import type { MachineTrace, TraceEvent } from "../trace-inspector";
import {
  extractConsumerInfo,
  extractFlowOutcome,
  extractIntakeResponses,
  reconstructFinalContext,
} from "./context-extraction";
import { generateTestActions } from "./dom-actions";
import { extractMetadata, generateTestName } from "./metadata";
import type { GeneratedTest, TestMetadata } from "./types";
import { detectUserFork, generateExistingUserSetup } from "./user-fork";
import { escapeString } from "./utils";

// ============================================================================
// Main Generator Function
// ============================================================================

/**
 * Generate a Playwright test from a machine trace
 */
export function generatePlaywrightTest(trace: MachineTrace): GeneratedTest {
  const metadata = extractMetadata(trace);
  const testName = generateTestName(trace);
  const testCode = generateTestCode(trace, testName, metadata);

  return {
    testName,
    testCode,
    metadata,
  };
}

/**
 * Generate test from a trace JSON string
 */
export function generateTestFromJSON(jsonString: string): GeneratedTest {
  const trace = JSON.parse(jsonString) as MachineTrace;
  return generatePlaywrightTest(trace);
}

// ============================================================================
// Test Code Generation
// ============================================================================

/**
 * Generate the complete test code from trace data
 */
function generateTestCode(
  trace: MachineTrace,
  testName: string,
  metadata: TestMetadata,
): string {
  // ========================================================================
  // Isomorphic Property Testing: Detect fork and extract consumer info
  // ========================================================================
  const userFork = detectUserFork(metadata.statePath);
  const consumerInfo = extractConsumerInfo(trace);
  const flowOutcome = extractFlowOutcome(trace);
  const intakeResponses = extractIntakeResponses(trace);
  const isExistingUser = userFork === "existing_user";

  // Build event list including async transitions
  // Same logic as TraceTab - include user events and async completion snapshots
  const machineEvents: TraceEvent[] = [];
  let lastUserEventToState: string | undefined;

  for (const event of trace.events) {
    if (event.inspectionType === "@xstate.event") {
      machineEvents.push(event);
      if (event.toState) {
        lastUserEventToState = event.toState;
      }
    } else if (event.inspectionType === "@xstate.snapshot") {
      // Include snapshots that represent async transitions
      if (!event.toState || !event.fromState) continue;
      if (
        event.fromState === lastUserEventToState &&
        event.toState !== event.fromState
      ) {
        machineEvents.push({
          ...event,
          machineEvent: { type: "done.invoke" },
        });
        lastUserEventToState = event.toState;
      }
    }
  }

  // Build the test actions from DOM events
  const actions = generateTestActions(machineEvents, isExistingUser);

  // Build final state assertion
  const finalContext = reconstructFinalContext(trace);
  const contextAssertions = generateContextAssertions(
    finalContext,
    metadata.finalState,
  );

  // Generate S3 fixture comment for test replay
  const s3FixtureComment = `
 * S3 Fixture (for test replay):
 * - Fixture ID: ${metadata.fixtureId}
 *
 * To use mock mode: Use consumer lastName "TESTSTHREE${metadata.fixtureId}" (letters only - EHR compatible)
 * The pricing gateway checks lastName (not memberId) because getInsuranceStatus
 * overwrites insurance data including memberId. lastName persists through the flow.
 *`;

  // Generate fork-specific comment
  const forkComment = isExistingUser
    ? `
 * User Fork: EXISTING USER (no OTP in state path)
 * - User/consumer/insurance pre-created in database via STU builders
 * - Kiosk will recognize user and skip onboarding steps
 *`
    : `
 * User Fork: NEW USER (OTP verified)
 * - User will be created during kiosk flow
 * - Consumer data randomized for privacy
 *`;

  // Generate OCR-specific comment and test.use() block
  const ocrOutcome = metadata.ocrOutcome;
  const ocrComment = ocrOutcome
    ? `
 * OCR Flow: ${ocrOutcome.flow.toUpperCase()} - ${ocrOutcome.outcome.toUpperCase()}
 * - Video file: ${ocrOutcome.videoFile}
 * - This test uses a fake camera with pre-recorded video for OCR testing
 *`
    : "";

  const ocrTestUseBlock = ocrOutcome
    ? `
// OCR Test Configuration: Fake camera with ${ocrOutcome.outcome} video
test.use({
  ...devices["Desktop Chrome"],
  permissions: ["camera"],
  launchOptions: {
    args: [
      "--use-fake-ui-for-media-stream",
      "--use-fake-device-for-media-stream",
      "--use-file-for-fake-video-capture=${ocrOutcome.videoFile}",
    ],
  },
  // biome-ignore lint/correctness/noEmptyPattern: Required for Playwright fixture override
  connectOptions: [async ({}, use) => use(undefined), { scope: "worker" }],
});
`
    : "";

  // Generate imports based on fork
  const stuImports = isExistingUser
    ? `import {
  DataSetup,
  DatabaseVerificationHelper,
  HttpAdapter,
  UserAccountStatus,
  ConsumerAccountStatus,
  getTestConfig,
  InsurancesStatusEnum,
  InsuranceAccountStatus,
  UserBuilder,
  ConsumerBuilder,
  InsuranceBuilder,
} from "@rsup2/stu";`
    : `import {
  DataSetup,
  DatabaseVerificationHelper,
  HttpAdapter,
  getTestConfig,
} from "@rsup2/stu";`;

  // Generate verification shortcut import
  const verificationImport = `import { verifyKioskFlowCompleted } from "../verificationShortcuts";`;

  // Generate existing user setup code if needed
  const existingUserSetup = isExistingUser
    ? generateExistingUserSetup(consumerInfo, intakeResponses)
    : "";

  return `/**
 * AUTO-GENERATED PLAYWRIGHT TEST
 * ==============================
 *
 * Generated from trace: ${metadata.traceId}
 * Generated at: ${metadata.generatedAt}
 * Pull Request: {{PR_URL}}
 *
 * Trace metadata:
 * - Initial state: ${metadata.initialState}
 * - Final state: ${metadata.finalState}
 * - Machine events: ${metadata.eventCount}
 * - DOM events captured: ${metadata.domEventCount}
 * - Duration: ${metadata.duration}
 *
 * State path (for deduplication):
 * ${metadata.statePath.join(" → ")}
 *
 * Fingerprint: ${metadata.statePathFingerprint}
 *${s3FixtureComment}${forkComment}${ocrComment}
 * This test was generated from real user interactions captured by the DOM Event Spy.
 * The DOM selectors are Playwright-compatible and should work as-is.
 */

import { ${ocrOutcome ? "devices, " : ""}expect, test } from "${
    ocrOutcome ? "../../fixtures" : "@playwright/test"
  }";
import { format } from "date-fns";

${stuImports}
${verificationImport}
import { type Consumer, generateRandomConsumerData, verify } from "../../utils";

// S3 Fixture ID for deterministic API responses (insurance/pricing)
const FIXTURE_ID = "${metadata.fixtureId}";
${
  ocrOutcome?.capturedData
    ? `
// OCR data captured from trace - used to mock /api/ocr/analyze-id response
const CAPTURED_OCR_DATA = ${JSON.stringify(ocrOutcome.capturedData, null, 2)};
`
    : ""
}
// Flow outcome captured from trace - used for database verification
const EXPECTED_TREATMENT_NAME = ${
    flowOutcome.treatmentName
      ? `"${escapeString(flowOutcome.treatmentName)}"`
      : "null"
  };
const EXPECTED_TREATMENT_SOURCE = "kiosk";
const EXPECTED_PRICE = ${
    flowOutcome.price !== null ? flowOutcome.price : "null"
  };
const EXPECTED_PAYMENT_SUCCEEDED = ${flowOutcome.paymentSucceeded};

const stuConfig = getTestConfig();
let dataSetup: DataSetup;
let httpAdapter: HttpAdapter;
let dbVerificationHelper: DatabaseVerificationHelper;

let consumer: Consumer;

// Stripe simulated reader ID for POS payment testing
// IMPORTANT: This must be a simulated reader (created with registration_code: 'simulated-wpe')
// Real readers will fail with "non simulated readers cannot be mocked" error
const SIMULATED_READER_ID = process.env.STRIPE_SIMULATED_READER_ID;
${ocrTestUseBlock}
test.beforeEach(async ({ page }) => {
  dataSetup = new DataSetup(stuConfig);
  httpAdapter = new HttpAdapter(stuConfig);
  dbVerificationHelper = new DatabaseVerificationHelper(dataSetup);

  consumer = generateRandomConsumerData();
${existingUserSetup}
${
  ocrOutcome?.capturedData
    ? `
  // Mock OCR API to return captured data from trace
  // This bypasses the real Textract call and returns pre-recorded OCR results
  await page.route('**/api/ocr/analyze-id', async (route) => {
    // Convert captured OCR data to Textract AnalyzeID response format
    const mockResponse = {
      response: {
        IdentityDocuments: [{
          IdentityDocumentFields: [
            { Type: { Text: "FIRST_NAME" }, ValueDetection: { Text: consumer.firstName || "", Confidence: 99 } },
            { Type: { Text: "LAST_NAME" }, ValueDetection: { Text: consumer.lastName || "", Confidence: 99 } },
            { Type: { Text: "MIDDLE_NAME" }, ValueDetection: { Text: CAPTURED_OCR_DATA.middleName || "", Confidence: 99 } },
            { Type: { Text: "DATE_OF_BIRTH" }, ValueDetection: { Text: format(consumer.dateOfBirth, "MM/dd/yyyy"), Confidence: 99 } },
            { Type: { Text: "ADDRESS" }, ValueDetection: { Text: consumer.address?.streetAddress || "", Confidence: 99 } },
            { Type: { Text: "CITY_IN_ADDRESS" }, ValueDetection: { Text: consumer.address?.city || "", Confidence: 99 } },
            { Type: { Text: "STATE_IN_ADDRESS" }, ValueDetection: { Text: consumer.address?.stateAbbreviated || "", Confidence: 99 } },
            { Type: { Text: "ZIP_CODE_IN_ADDRESS" }, ValueDetection: { Text: consumer.address?.zipCode || "", Confidence: 99 } },
            { Type: { Text: "ID_NUMBER" }, ValueDetection: { Text: CAPTURED_OCR_DATA.idNumber || "", Confidence: 99 } },
            { Type: { Text: "EXPIRATION_DATE" }, ValueDetection: { Text: CAPTURED_OCR_DATA.expirationDate || "", Confidence: 99 } },
          ],
        }],
      },
    };
    await route.fulfill({
      status: 200,
      contentType: 'application/json',
      body: JSON.stringify(mockResponse),
    });
  });

  // Mock gender query (separate Textract call for sex field)
  await page.route('**/api/ocr/analyze-document', async (route) => {
    const mockResponse = {
      response: {
        Blocks: [
          { BlockType: "QUERY", Query: { Text: "What is the person's gender?" }, Id: "q1", Relationships: [{ Type: "ANSWER", Ids: ["a1"] }] },
          { BlockType: "QUERY_RESULT", Id: "a1", Text: CAPTURED_OCR_DATA.sex === "M" ? "Male" : CAPTURED_OCR_DATA.sex === "F" ? "Female" : CAPTURED_OCR_DATA.sex || "", Confidence: 99 },
        ],
      },
    };
    await route.fulfill({
      status: 200,
      contentType: 'application/json',
      body: JSON.stringify(mockResponse),
    });
  });
`
    : ""
}
  // Strip Vercel headers from Google API requests to prevent CORS failures.
  // Playwright's extraHTTPHeaders adds x-vercel-* headers to ALL requests,
  // but Google's Places API rejects requests with unexpected headers.
  await page.route('**/*.googleapis.com/**', async (route) => {
    const {
      'x-vercel-protection-bypass': _1,
      'x-vercel-set-bypass-cookie': _2,
      'x-vercel-skip-toolbar': _3,
      ...headers
    } = route.request().headers();
    await route.continue({ headers });
  });

  // Navigate with simulated reader ID for Stripe test helper compatibility
  // The reader ID must be a simulated reader, not a real physical reader
  const startUrl = SIMULATED_READER_ID ? \`/?terminalId=\${SIMULATED_READER_ID}\` : "/";
  await page.goto(startUrl);
  await verify(async () => expect(page.url()).toContain("?stateId="));
});

test.afterEach(async () => {
  try {
    await dbVerificationHelper.populateDataSetupForTeardown(
      consumer.firstName,
      consumer.lastName,
      consumer.phone
    );
    await dataSetup.cleanup();
  } catch (error) {
    console.warn(
      "Error during teardown. Test likely did not make it to the point of writing to the database."
    );
  }
});

test("${escapeString(testName)}", async ({ page }) => {
${actions}

  // Wait for the state machine to process the final action
  // The last DOM action (e.g., button click) triggers a state transition,
  // but context updates happen asynchronously after the event is processed
  await page.waitForTimeout(1000);

  // ============================================================================
  // Final State Assertions
  // ============================================================================
${contextAssertions}

  // ============================================================================
  // Database Verification (End-to-End Property Testing)
  // Verifies that the kiosk flow wrote the correct data to the database:
  // - User/Consumer created with correct PII
  // - Insurance records match
  // - Appointment created with correct treatment
  // - Charge created with correct price
  // - Payment transaction recorded
  // - EHR patient and appointment synced
  // ============================================================================
  if (EXPECTED_TREATMENT_NAME && EXPECTED_PRICE !== null) {
    await verifyKioskFlowCompleted({
      dbVerificationHelper,
      consumer,
      expectedTreatmentName: EXPECTED_TREATMENT_NAME,
      expectedTreatmentSource: EXPECTED_TREATMENT_SOURCE,
      expectedPrice: EXPECTED_PRICE,
      expectedPaymentStatus: EXPECTED_PAYMENT_SUCCEEDED,
    });
  }
});
`;
}

// ============================================================================
// Context Assertions
// ============================================================================

/**
 * Generate assertions for the final state
 * Only asserts on the machine state value, not context (which contains session-specific data)
 */
function generateContextAssertions(
  _context: Record<string, unknown>,
  finalState: string,
): string {
  const lines: string[] = [];

  lines.push("  // Verify we reached the expected final state");
  lines.push("  const finalMachineState = await page.evaluate(() => {");
  lines.push(
    "    const stateId = new URL(window.location.href).searchParams.get('stateId');",
  );
  lines.push(
    "    const entry = JSON.parse(localStorage.getItem(`kiosk-state:${stateId}`) || '{}');",
  );
  lines.push("    return entry.state?.value;");
  lines.push("  });");
  lines.push("");
  lines.push(`  expect(finalMachineState).toEqual("${finalState}");`);

  return lines.join("\n");
}
